import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@pentabd/ui";

import { HeaderWithNextButton, MultiFileRow, NextButton } from "@components";
import { NOAIssue, TenderSecurity } from "./components";

import { useNOA } from "@hooks/procurements/procurement/createProcurements/useNOA";
import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";
import {
  convertDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

import {
  NOADetailResponseType,
  NOAType,
} from "@type/procurements/createProcurement/NOA";

import { FORM_FIELDS } from "@constants/form";

export const NOA_FROM = FORM_FIELDS.PROCUREMENT_DETAILS.NOA;

export interface NOAPropType {
  id: any;
  changeActiveTab: (arg: number) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}

const NOA = ({
  id,
  changeActiveTab,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
}: NOAPropType) => {
  const { t } = useTranslation();
  const { createNOA, NOAData } = useNOA(id);

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { getSingleData, createError, createSuccess } =
    NOAData || ({} as NOADetailResponseType);

  const { data } = getSingleData || ({} as NOAType);

  const method = useForm({
    values: {
      ...(revertMultipleDocumentData(data, [NOA_FROM.DOCUMENT]) as NOAType),
      performanceSecAmount:
        data?.[NOA_FROM.PERFORMANCE_SECURITY_AMOUNT] || data?.securityMoney,
    },
  });

  const { handleSubmit } = method;

  const { control, register, setValue } = method;

  const onSubmit: SubmitHandler<any> = (data: any) => {
    const reqData = convertDocumentData(data, NOA_FROM.DOCUMENT);

    createNOA(reqData, id, data?.id);
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      default: {
        setIsSuccessModalOpen(false);
        break;
      }
    }
  }, [createSuccess, createError]);

  return (
    <div className="p-10">
      <HeaderWithNextButton
        title="PROCUREMENT_CREATE.NOA.HEADER.TITLE"
        action={() => changeActiveTab(6)}
      />
      <FormProvider {...method}>
        <form
          className="container d-flex flex-column gap-6 py-10 border-bottom"
          onSubmit={handleSubmit(onSubmit)}
        >
          <NOAIssue />
          <TenderSecurity
            securityMoney={
              data?.[NOA_FROM.PERFORMANCE_SECURITY_AMOUNT] ||
              data?.securityMoney
            }
          />

          <MultiFileRow
            name={NOA_FROM.DOCUMENT}
            control={control}
            setValue={setValue}
            register={register}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />

          <div className="row">
            <div className="col-lg-3"></div>

            {getAccess(roles, "PROCUREMENT_HR") && (
              <div className="d-flex justify-content-end gap-8 col-lg-7">
                <Button
                  fill="outline"
                  type="primary"
                  htmlType="submit"
                  size="sm"
                >
                  {t("PROCUREMENT_CREATE.NOA.FORM.SAVE_BUTTON_TEXT")}
                </Button>
              </div>
            )}
          </div>
        </form>
        <div className="text-end mt-10">
          <NextButton action={() => changeActiveTab(6)} />
        </div>
      </FormProvider>
    </div>
  );
};

export default NOA;
